import type { ExpRateChartGap, ExpRateChartSnapshot, ExpReadoutSnapshot, ExpRecognitionIssue, ExpRecognitionProgress, RecognitionMetricKey } from '@/domain/types'
import type { PresenceBreakReason } from '@/core/source-continuity'
import type { LayoutSearchDiagnostic } from '@/presets/types'
import { buildInfo } from './build-info'
import type { OcrStartupDiagnostic } from './ocr-startup'

export type PerformanceStage = 'frame' | 'crop' | 'preprocess' | 'ocr' | 'ocr-initialize' | 'chart' | 'save' | 'long-task'
  | 'widget-render' | 'widget-layout' | 'widget-composition' | 'widget-heartbeat'
export type WidgetRefreshReason = 'initial' | 'snapshot' | 'timer' | 'explicit' | 'command'
export interface WidgetPerformanceContext {
  open: boolean
  mode: 'pip' | 'popup' | 'preview'
  skin: string
  mainVisibility: DocumentVisibilityState
}
export type PerformanceResource = 'live-stores' | 'previews' | 'charts' | 'pending-saves' | 'frame-sources' | 'layout-workers' | 'ocr-model-urls'
export type DiagnosticError = 'capture-error' | 'frame-error' | 'image-error' | 'ocr-timeout' | 'ocr-engine-error' | 'ocr-initialize-error' | 'ocr-unknown-error' | 'cancelled'
export interface WidgetFailure {
  phase: 'controls' | 'content' | 'layout' | 'command' | 'heartbeat'
  errorKind: string
  skin: string
  durationMs: number
  sessionStatus: string
  isEnding: boolean
  isStarting: boolean
  reloadBlocked: boolean
  pendingCommand: 'primary' | 'end' | null
  contentFailures: number
}

type Counter = 'ocrInitializations' | 'ocrResets' | 'unchangedSkips' | 'recoveryBackoffs'
interface Measurement { at: number; ms: number }
interface Timing { count: number; totalMs: number; maxMs: number; recent: Measurement[] }
interface ExpChartEvent extends ExpRateChartGap {
  to: number
  observedAt: number
  durationMs: number
  readoutReason: ExpReadoutSnapshot['reason']
  recoveredAt: number | null
  recoverySourceAt: number | null
  recognitionIssue?: ExpRecognitionIssue
}
type ExpRecognitionEvent = ExpRecognitionIssue & { lastSourceAt: number; recoveredAt: number | null; recoverySourceAt: number | null }
export interface CapturePerformanceInfo {
  status: string
  requestedFpsLimit: number
  reportedFps: number | null
  width: number | null
  height: number | null
  surface: string | null
  videoWidth?: number | null
  videoHeight?: number | null
  constraintTier?: string | null
  constraintTargetFps?: number | null
  constraintApplied?: boolean
  constraintMaxFps?: number | null
  constraintFailure?: string | null
  playbackReportedFps?: number | null
  playbackConstraintMaxFps?: number | null
}

const WINDOW_MS = 60_000
const MAX_SAMPLES = 240
const MAX_EVENTS = 40
const stages: PerformanceStage[] = ['frame', 'crop', 'preprocess', 'ocr', 'ocr-initialize', 'chart', 'save', 'long-task',
  'widget-render', 'widget-layout', 'widget-composition', 'widget-heartbeat']

/** Bounded, in-memory metadata only. No images, recognized text, titles or URLs. */
export class PerformanceDiagnostics {
  private widgetContext: WidgetPerformanceContext | null = null
  private widgetRefreshes: { at: number; reason: WidgetRefreshReason; rendered: boolean; mode: WidgetPerformanceContext['mode']; mainVisibility: DocumentVisibilityState }[] = []
  private widgetRefreshCounts: Partial<Record<WidgetRefreshReason, { rendered: number; skipped: number }>> = {}
  setWidgetContext(value: WidgetPerformanceContext): void { this.widgetContext = { ...value } }
  closeWidget(): void {
    if (this.widgetContext) this.widgetContext = { ...this.widgetContext, open: false, mainVisibility: document.visibilityState }
  }
  widgetRefresh(reason: WidgetRefreshReason, rendered: boolean): void {
    const counts = this.widgetRefreshCounts[reason] ??= { rendered: 0, skipped: 0 }
    counts[rendered ? 'rendered' : 'skipped']++
    if (!this.widgetContext) return
    this.widgetRefreshes.push({ at: Date.now(), reason, rendered, mode: this.widgetContext.mode, mainVisibility: this.widgetContext.mainVisibility })
    if (this.widgetRefreshes.length > MAX_EVENTS) this.widgetRefreshes.shift()
  }
  private widgetFailures: (WidgetFailure & { at: number })[] = []
  widgetFailure(value: WidgetFailure): void {
    this.widgetFailures.push({ ...value, at: Date.now() })
    if (this.widgetFailures.length > MAX_EVENTS) this.widgetFailures.shift()
  }

  private resources: Partial<Record<PerformanceResource, { current: number; peak: number }>> = {}
  /** Return an idempotent release function; retain only numeric counts here. */
  acquire(resource: PerformanceResource): () => void {
    const value = this.resources[resource] ??= { current: 0, peak: 0 }
    value.current++
    value.peak = Math.max(value.peak, value.current)
    let released = false
    return () => {
      if (released) return
      released = true
      value.current--
    }
  }

  observeLongTasks(): () => void {
    if (typeof PerformanceObserver === 'undefined' || !PerformanceObserver.supportedEntryTypes?.includes('longtask')) return () => {}
    let observer: PerformanceObserver | null = null
    try {
      observer = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) this.measure('long-task', entry.duration)
      })
      observer.observe({ type: 'longtask' })
    } catch { observer?.disconnect(); observer = null }
    return () => { observer?.disconnect(); observer = null }
  }
  private ocrStartup: OcrStartupDiagnostic | null = null
  setOcrStartup(value: OcrStartupDiagnostic): void { this.ocrStartup = structuredClone(value) }
  private layout: Partial<Record<'hud' | 'map' | 'mesos', LayoutSearchDiagnostic>> = {}
  observeLayout(group: 'hud' | 'map' | 'mesos', diagnostic: LayoutSearchDiagnostic): void {
    this.layout[group] = { ...diagnostic }
  }
  private metricSources = new Map<RecognitionMetricKey, number>()
  private metricIntervals = new Map<RecognitionMetricKey, { count: number; totalMs: number; lastMs: number; maxMs: number }>()
  private continuityBreaks: { at: string; metric: string; reason: string }[] = []
  private continuityCounts: Record<string, number> = {}
  private mapConfirmation: { state: string; reason?: string; expirationReason?: string; sampleGapMs?: number; windowMs?: number; expiresAt?: number } | null = null
  private startWait: { stage: string; since: number; budgetMs?: number } | null = null
  private expSession: string | null = null
  private expState: { status: string; reason: string | null; scope: string; since: number } | null = null
  private expIssueSince: number | null = null
  private expCounts: Record<string, number> = {}
  private expDurations: Record<string, number> = {}
  private expEvents: { at: number; status: string; reason: string | null; durationMs: number; recoveryMs: number | null }[] = []
  private expChartSession: string | null = null
  private expChartEvents: ExpChartEvent[] = []
  private expRecognitionSession: string | null = null
  private expRecognition: ExpRecognitionProgress | null = null
  private expRecognitionEvents: ExpRecognitionEvent[] = []
  private startedAt = Date.now()
  private timings = new Map<PerformanceStage, Timing>()
  private counters: Record<Counter, number> = { ocrInitializations: 0, ocrResets: 0, unchangedSkips: 0, recoveryBackoffs: 0 }
  private errorCounts: Partial<Record<DiagnosticError, number>> = {}
  private events: { at: string; code: DiagnosticError; retryInMs: number }[] = []
  private captureReader: (() => CapturePerformanceInfo) | null = null
  private processingIntervalMs: number | null = null

  observeMetric(metric: RecognitionMetricKey, sourceAt: number): void {
    if (!Number.isFinite(sourceAt)) return
    const previous = this.metricSources.get(metric)
    if (previous !== undefined && sourceAt <= previous) return
    this.metricSources.set(metric, sourceAt)
    if (previous === undefined) return
    const gap = sourceAt - previous
    const value = this.metricIntervals.get(metric) ?? { count: 0, totalMs: 0, lastMs: 0, maxMs: 0 }
    value.count += 1; value.totalMs += gap; value.lastMs = gap; value.maxMs = Math.max(value.maxMs, gap)
    this.metricIntervals.set(metric, value)
  }
  breakContinuity(metric: string, reason: PresenceBreakReason | 'legacy-timeout' | 'paused' | 'generation-changed'): void {
    const key = `${metric}:${reason}`
    this.continuityCounts[key] = (this.continuityCounts[key] ?? 0) + 1
    this.continuityBreaks.push({ at: new Date().toISOString(), metric, reason })
    if (this.continuityBreaks.length > MAX_EVENTS) this.continuityBreaks.shift()
  }
  setMapConfirmation(value: NonNullable<PerformanceDiagnostics['mapConfirmation']>): void { this.mapConfirmation = { ...value } }
  setStartWait(value: PerformanceDiagnostics['startWait']): void { this.startWait = value ? { ...value } : null }
  observeExpRecognition(sessionId: string, progress: ExpRecognitionProgress, now = Date.now()): void {
    if (sessionId !== this.expRecognitionSession) {
      this.expRecognitionSession = sessionId; this.expRecognition = null; this.expRecognitionEvents = []
    }
    const issue = progress.lastIssue, previous = this.expRecognition?.lastIssue
    if (issue) {
      const last = this.expRecognitionEvents.at(-1)
      if (last && previous && last.recoveredAt === null && issue.kind === last.kind && issue.reason === last.reason) {
        last.lastSourceAt = Math.max(last.lastSourceAt, issue.sourceAt)
      } else {
        this.expRecognitionEvents.push({ ...issue, lastSourceAt: issue.sourceAt, recoveredAt: null, recoverySourceAt: null })
        if (this.expRecognitionEvents.length > MAX_EVENTS) this.expRecognitionEvents.shift()
      }
    } else if (progress.lastAcceptedSourceAt !== null) {
      for (const event of this.expRecognitionEvents) {
        if (event.recoveredAt === null && event.lastSourceAt <= progress.lastAcceptedSourceAt) {
          event.recoveredAt = now; event.recoverySourceAt = progress.lastAcceptedSourceAt
        }
      }
    }
    this.expRecognition = { ...progress, lastIssue: issue ? { ...issue } : null }
  }
  observeExpChart(sessionId: string, chart: ExpRateChartSnapshot | undefined, readout: ExpReadoutSnapshot, now = Date.now()): void {
    if (sessionId !== this.expChartSession) { this.expChartSession = sessionId; this.expChartEvents = [] }
    if (!chart) return
    for (const gap of chart.gaps) {
      let event = this.expChartEvents.find(e => e.reason === gap.reason && e.from === gap.from)
      if (!event) {
        event = { ...gap, observedAt: now, durationMs: 0, readoutReason: null, recoveredAt: null, recoverySourceAt: null }
        this.expChartEvents.push(event)
        if (this.expChartEvents.length > MAX_EVENTS) this.expChartEvents.shift()
      }
      event.to = gap.to
      event.durationMs = Math.max(0, gap.to - gap.from)
      if (gap.reason !== 'warming' && !event.recognitionIssue && sessionId === this.expRecognitionSession) {
        const issue = this.expRecognitionEvents.find(e => e.sourceAt >= gap.from && e.sourceAt <= gap.to)
        if (issue) event.recognitionIssue = { kind: issue.kind, reason: issue.reason, sourceAt: issue.sourceAt, observedAt: issue.observedAt } as ExpRecognitionIssue
      }
      if (gap.reason !== 'warming' && event.recoveredAt === null
        && gap.to >= (chart.recent.at ?? chart.to) && readout.reason) event.readoutReason ??= readout.reason
      const completed = gap.reason === 'warming' ? event.durationMs >= (chart.recent.windowMs ?? WINDOW_MS)
        : chart.recent.at !== null && chart.recent.at >= gap.to && readout.status === 'ready'
      if (completed && event.recoveredAt === null) {
        event.recoveredAt = now
        event.recoverySourceAt = chart.recent.at
      }
    }
  }
  observeExpReadout(sessionId: string, value: ExpReadoutSnapshot, now = Date.now()): void {
    if (sessionId !== this.expSession) {
      this.expSession = sessionId; this.expState = null; this.expIssueSince = null
    }
    const previous = this.expState
    if (previous?.status === value.status && previous.reason === value.reason && previous.scope === value.scope) return
    const durationMs = previous ? Math.max(0, now - previous.since) : 0
    if (previous) this.expDurations[previous.status] = (this.expDurations[previous.status] ?? 0) + durationMs
    if (value.status !== 'ready') this.expIssueSince ??= now
    const recoveryMs = value.status === 'ready' && this.expIssueSince !== null ? Math.max(0, now - this.expIssueSince) : null
    if (value.status === 'ready') this.expIssueSince = null
    const key = `${value.status}:${value.reason ?? 'none'}`
    this.expCounts[key] = (this.expCounts[key] ?? 0) + 1
    this.expEvents.push({ at: now, status: value.status, reason: value.reason, durationMs, recoveryMs })
    if (this.expEvents.length > MAX_EVENTS) this.expEvents.shift()
    this.expState = { status: value.status, reason: value.reason, scope: value.scope, since: now }
  }
  setCaptureReader(reader: () => CapturePerformanceInfo): void { this.captureReader = reader }
  setProcessingInterval(intervalMs: number | null): void { this.processingIntervalMs = intervalMs }
  increment(counter: Counter): void { this.counters[counter] += 1 }

  measure(stage: PerformanceStage, ms: number): void {
    if (!Number.isFinite(ms) || ms < 0) return
    const timing = this.timings.get(stage) ?? { count: 0, totalMs: 0, maxMs: 0, recent: [] }
    timing.count += 1
    timing.totalMs += ms
    timing.maxMs = Math.max(timing.maxMs, ms)
    timing.recent.push({ at: Date.now(), ms })
    if (timing.recent.length > MAX_SAMPLES) timing.recent.shift()
    this.timings.set(stage, timing)
  }

  error(code: DiagnosticError, retryInMs = 0): void {
    this.errorCounts[code] = (this.errorCounts[code] ?? 0) + 1
    this.events.push({ at: new Date().toISOString(), code, retryInMs })
    if (this.events.length > MAX_EVENTS) this.events.shift()
  }

  clear(): void {
    for (const value of Object.values(this.resources)) value.peak = value.current
    this.ocrStartup = null
    this.layout = {}
    this.startedAt = Date.now()
    this.metricSources.clear(); this.metricIntervals.clear(); this.continuityBreaks = []; this.continuityCounts = {}
    this.mapConfirmation = null; this.startWait = null
    this.expSession = null; this.expState = null; this.expIssueSince = null
    this.expCounts = {}; this.expDurations = {}; this.expEvents = []
    this.expChartSession = null; this.expChartEvents = []
    this.expRecognitionSession = null; this.expRecognition = null; this.expRecognitionEvents = []
    this.timings.clear()
    this.counters = { ocrInitializations: 0, ocrResets: 0, unchangedSkips: 0, recoveryBackoffs: 0 }
    this.errorCounts = {}
    this.events = []
    this.widgetFailures = []
    this.widgetRefreshes = []; this.widgetRefreshCounts = {}
  }

  snapshot() {
    const now = Date.now()
    let capture: CapturePerformanceInfo | null = null
    try { capture = this.captureReader?.() ?? null } catch { /* closed or unavailable track */ }
    const timings = Object.fromEntries(stages.map(stage => {
      const timing = this.timings.get(stage)
      const recent = (timing?.recent ?? []).filter(sample => now - sample.at <= WINDOW_MS)
      const sorted = recent.map(sample => sample.ms).sort((a, b) => a - b)
      return [stage, {
        count: timing?.count ?? 0,
        averageMs: timing?.count ? round(timing.totalMs / timing.count) : null,
        maxMs: timing?.count ? round(timing.maxMs) : null,
        recentSampleCount: recent.length,
        recentP95Ms: sorted.length ? round(sorted[Math.ceil(sorted.length * .95) - 1]!) : null,
      }]
    }))
    return {
      schemaVersion: 2,
      build: buildInfo,
      ocrStartup: this.ocrStartup ? structuredClone(this.ocrStartup) : null,
      layout: structuredClone(this.layout),
      startedAt: new Date(this.startedAt).toISOString(),
      generatedAt: new Date(now).toISOString(),
      durationSeconds: round((now - this.startedAt) / 1_000),
      environment: { userAgent: navigator.userAgent, hardwareConcurrency: navigator.hardwareConcurrency ?? null, visibility: document.visibilityState, devicePixelRatio: window.devicePixelRatio ?? null },
      capture,
      processingIntervalMs: this.processingIntervalMs,
      timings,
      resources: structuredClone(this.resources),
      // Chromium-only, sampled on export: excludes Worker, WASM and GPU allocations.
      mainThreadHeap: readMainThreadHeap(),
      metricReadingIntervals: Object.fromEntries([...this.metricIntervals].map(([key, value]) => [key, {
        count: value.count, averageMs: round(value.totalMs / value.count), lastMs: value.lastMs, maxMs: value.maxMs,
      }])),
      continuity: { counts: { ...this.continuityCounts }, recentBreaks: this.continuityBreaks.map(value => ({ ...value })) },
      mapConfirmation: this.mapConfirmation ? { ...this.mapConfirmation } : null,
      startWait: this.startWait ? { ...this.startWait } : null,
      expReadout: { current: this.expState ? { ...this.expState, durationMs: Math.max(0, now - this.expState.since) } : null,
        counts: { ...this.expCounts }, completedDurationMs: { ...this.expDurations }, recentChanges: this.expEvents.map(event => ({ ...event })) },
      expChart: { recentGaps: structuredClone(this.expChartEvents) },
      expRecognition: { current: this.expRecognition ? structuredClone(this.expRecognition) : null,
        recentIssues: structuredClone(this.expRecognitionEvents) },
      counters: { ...this.counters },
      errorCounts: { ...this.errorCounts },
      recentErrors: this.events.map(event => ({ ...event })),
      widgetFailures: this.widgetFailures.map(event => ({ ...event })),
      widget: { context: this.widgetContext ? { ...this.widgetContext } : null,
        refreshCounts: structuredClone(this.widgetRefreshCounts), recentRefreshes: this.widgetRefreshes.map(event => ({ ...event })) },
      limits: { recentWindowSeconds: WINDOW_MS / 1_000, maxSamplesPerStage: MAX_SAMPLES, maxRecentErrors: MAX_EVENTS },
    }
  }
}

function readMainThreadHeap() {
  const memory = (performance as Performance & { memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } }).memory
  if (!memory || !Number.isFinite(memory.usedJSHeapSize)) return null
  return { usedBytes: memory.usedJSHeapSize, allocatedBytes: memory.totalJSHeapSize, limitBytes: memory.jsHeapSizeLimit,
    scope: 'main-thread-js-heap-only' as const }
}

function round(value: number): number { return Math.round(value * 100) / 100 }
export const performanceDiagnostics = new PerformanceDiagnostics()
